import React, { useState, useEffect, useMemo } from 'react';
import '../TracePanelStyles.css';
import { Typography } from '@material-ui/core';
import AdbIcon from '@material-ui/icons/Adb';
import CodeIcon from '@material-ui/icons/Code';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SaveIcon from '@material-ui/icons/Save';
import ChatIcon from '@material-ui/icons/Chat';
import { useTranslation } from 'react-i18next';
import {
    formatDuration,
    JsonView,
    DetailSection,
    TracePanelHeader,
    TraceDetailTabs,
    TraceSelectableRow,
    formatCountLabel,
    useLazyAnswerPanelData,
    PanelLoadingState,
    PanelLoadError
} from '../TracePanelShared';

const getExecutionDurationMs = (execution) => {
    const start = execution?.startedAt ? Date.parse(execution.startedAt) : NaN;
    const end = execution?.endedAt ? Date.parse(execution.endedAt) : NaN;
    if (Number.isNaN(start) || Number.isNaN(end)) {
        return null;
    }
    return Math.max(0, end - start);
};

const getExecutionKey = (execution, index) =>
    `${execution.nodeId || 'node'}-${execution.startedAt || ''}-${index}`;

const getNodeIcon = (nodeType) => {
    const type = (nodeType || '').toLowerCase();
    if (type.includes('javascript') || type.includes('script')) {
        return <CodeIcon className="icon-code" style={{ fontSize: 16 }} />;
    }
    if (type.includes('join')) {
        return <CallSplitIcon className="icon-condition" style={{ fontSize: 16 }} />;
    }
    if (type.includes('if')) {
        return <CallSplitIcon className="icon-condition" style={{ fontSize: 16 }} />;
    }
    if (type.includes('question')) {
        return <HelpOutlineIcon className="icon-node" style={{ fontSize: 16 }} />;
    }
    if (type.includes('save')) {
        return <SaveIcon className="icon-node" style={{ fontSize: 16 }} />;
    }
    if (type.includes('answer')) {
        return <ChatIcon className="icon-node" style={{ fontSize: 16 }} />;
    }
    return <AdbIcon className="icon-chain" style={{ fontSize: 16 }} />;
};

const NodeExecutionRow = ({ execution, isSelected, onSelect }) => {
    const durationMs = getExecutionDurationMs(execution);

    return (
        <TraceSelectableRow isSelected={isSelected} onSelect={onSelect}>
            <div className="trace-toggle" aria-hidden="true" />
            <div className="trace-icon">{getNodeIcon(execution.nodeType)}</div>
            <div className="trace-name">{execution.nodeType}</div>
            <div className="trace-pills">
                <span className="pill pill-duration">{formatDuration(durationMs)}</span>
            </div>
        </TraceSelectableRow>
    );
};

const ConversationItemNodeExecutions = ({ projectId, conversationId, answerId }) => {
    const { t, i18n } = useTranslation();
    const [detailTab, setDetailTab] = useState('input');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const {
        data: executions,
        isLoading,
        loadError,
        isExpanded,
        isLoaded,
        hasData,
        handleToggle,
        fetchData
    } = useLazyAnswerPanelData({
        projectId,
        conversationId,
        answerId,
        resource: 'node-executions',
        t
    });

    const executionList = useMemo(() => executions ?? [], [executions]);
    const countLabel = !isLoaded ? null : formatCountLabel(executionList.length, t, i18n, 'node');
    const selectedExecution = hasData ? (executionList[selectedIndex] ?? executionList[0]) : null;
    const durationMs = selectedExecution ? getExecutionDurationMs(selectedExecution) : null;

    useEffect(() => {
        if (selectedIndex >= executionList.length) {
            setSelectedIndex(0);
        }
    }, [executionList.length, selectedIndex]);

    useEffect(() => {
        if (hasData) {
            setSelectedIndex(0);
        }
    }, [executionList, hasData]);

    const detailTabs = [
        { id: 'input', label: t('Input') },
        { id: 'output', label: t('Output') },
        { id: 'attributes', label: t('Attributes') }
    ];

    return (
        <div className={`trace-container ${isExpanded ? 'is-expanded' : ''}`}>
            <TracePanelHeader
                title={t('Node Executions')}
                countLabel={countLabel}
                isExpanded={isExpanded}
                onToggle={handleToggle}
            />

            {isExpanded && (
                <div className="trace-content">
                    {isLoading ? (
                        <PanelLoadingState />
                    ) : loadError ? (
                        <PanelLoadError
                            message={loadError}
                            onRetry={fetchData}
                            retryLabel={t('Try again')}
                        />
                    ) : !hasData ? (
                        <div style={{ padding: '24px', color: '#70757a', fontSize: '13px', width: '100%', textAlign: 'center' }}>
                            {t('No node executions found for this conversation turn.')}
                        </div>
                    ) : (
                        <>
                            <div className="trace-tree">
                                {executionList.map((execution, index) => (
                                    <NodeExecutionRow
                                        key={getExecutionKey(execution, index)}
                                        execution={execution}
                                        isSelected={selectedIndex === index}
                                        onSelect={() => setSelectedIndex(index)}
                                    />
                                ))}
                            </div>
                            <div className="trace-detail">
                                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <div className="detail-header">
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '4px'
                                            }}
                                        >
                                            {getNodeIcon(selectedExecution.nodeType)}
                                            <Typography
                                                variant="subtitle1"
                                                style={{ color: '#3c4043', fontWeight: 600 }}
                                            >
                                                {selectedExecution.nodeType}
                                            </Typography>
                                        </div>
                                        <Typography variant="caption" style={{ color: '#70757a' }}>
                                            ID: {selectedExecution.nodeId}
                                        </Typography>
                                    </div>
                                    <TraceDetailTabs
                                        tabs={detailTabs}
                                        activeTab={detailTab}
                                        onTabChange={setDetailTab}
                                    />
                                    <div className="detail-body">
                                        {detailTab === 'input' && (
                                            <DetailSection title={t('Inputs')} defaultExpanded={true}>
                                                <JsonView data={selectedExecution.inputs} />
                                            </DetailSection>
                                        )}
                                        {detailTab === 'output' && (
                                            <DetailSection title={t('Outputs')} defaultExpanded={true}>
                                                <JsonView data={selectedExecution.outputs} />
                                            </DetailSection>
                                        )}
                                        {detailTab === 'attributes' && (
                                            <DetailSection title={t('Metadata')} defaultExpanded={true}>
                                                <div className="detail-meta-row">
                                                    <div className="detail-meta-key">nodeType</div>
                                                    <div className="detail-meta-value">
                                                        {selectedExecution.nodeType}
                                                    </div>
                                                </div>
                                                <div className="detail-meta-row">
                                                    <div className="detail-meta-key">nodeId</div>
                                                    <div className="detail-meta-value">
                                                        {selectedExecution.nodeId}
                                                    </div>
                                                </div>
                                                <div className="detail-meta-row">
                                                    <div className="detail-meta-key">startedAt</div>
                                                    <div className="detail-meta-value">
                                                        {selectedExecution.startedAt || '—'}
                                                    </div>
                                                </div>
                                                <div className="detail-meta-row">
                                                    <div className="detail-meta-key">endedAt</div>
                                                    <div className="detail-meta-value">
                                                        {selectedExecution.endedAt || '—'}
                                                    </div>
                                                </div>
                                                <div className="detail-meta-row">
                                                    <div className="detail-meta-key">duration</div>
                                                    <div className="detail-meta-value">
                                                        {formatDuration(durationMs)}
                                                    </div>
                                                </div>
                                            </DetailSection>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConversationItemNodeExecutions;
